import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import apiClient from '../../core/config/api';
import "./credit-logs.css";
import "./email_verify.css";

// Reached from Manage Users (Credit Logs button); Back returns there.
const CreditLogs = () => {
    const navigate = useNavigate();
    const [creditLogs, setCreditLogs] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [appliedSearch, setAppliedSearch] = useState("");
    const [page, setPage] = useState(1);
    const [pageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [goToPageInput, setGoToPageInput] = useState("");
    const [totalLogs, setTotalLogs] = useState(0);
    const [totalCreditsUsed, setTotalCreditsUsed] = useState(0);
    const [activeUsers, setActiveUsers] = useState(0);
    const [inactiveUsers, setInactiveUsers] = useState(0);

    useEffect(() => {
        fetchCreditLogs();
    }, [page, pageSize, appliedSearch]);

    const fetchCreditLogs = async () => {
        try {
            setLoading(true);
            setError("");
            const params: Record<string, string | number> = { page, page_size: pageSize };
            if (appliedSearch.trim()) params.search = appliedSearch.trim();

            const response = await apiClient.get('/re/sub-users/credit-logs', { params });
            const logs = response.data.data || [];
            const summary = response.data.summary || {};

            setCreditLogs(logs);
            setTotalPages(response.data.total_pages || 1);
            setTotalLogs(response.data.total || 0);
            setTotalCreditsUsed(summary.total_credits_used || 0);
            setActiveUsers(summary.active_users || 0);
            setInactiveUsers(summary.inactive_users || 0);
        } catch (err: any) {
            setError(err?.response?.data?.detail || 'Failed to load credit logs');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        setPage(1);
        setAppliedSearch(searchTerm);
    };

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
        }
    };

    const handleGoToPage = () => {
        const newPage = parseInt(goToPageInput);
        if (!isNaN(newPage) && newPage >= 1 && newPage <= totalPages) {
            handlePageChange(newPage);
            setGoToPageInput(String(newPage));
        }
    };

    const renderPageNumbers = () => {
        const pages: (number | string)[] = [];
        const maxVisiblePages = 5;

        if (totalPages <= maxVisiblePages) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else if (page <= 3) {
            pages.push(1, 2, 3, "...", totalPages);
        } else if (page >= totalPages - 2) {
            pages.push(1, "...", totalPages - 2, totalPages - 1, totalPages);
        } else {
            pages.push(1, "...", page, "...", totalPages);
        }

        return pages;
    };

    const formatTimestamp = (dateString: string) => {
        if (!dateString) return "-";
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    };

    // Pending / In-Progress / Failed pill for a request that hasn't finished -
    // the same one the Verification History on the Email Verifier page shows.
    const renderStatusPill = (status: string, progress: number) => {
        if (status === "failed") {
            return (
                <div className="bulk-status-pill bulk-status-failed">
                    <div className="bulk-status-pill-row">
                        <span>Failed</span>
                        <button
                            type="button"
                            className="bulk-status-retry"
                            onClick={() => navigate("/email-verifier?tab=bulk")}
                            title="Upload the file again"
                        >
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                                <path fillRule="evenodd" d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2v1z" />
                                <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z" />
                            </svg>
                            Retry
                        </button>
                    </div>
                    <div className="bulk-status-pill-track" />
                </div>
            );
        }
        let variant = "progress";
        let label = "In-Progress";
        const percent = Math.min(99, Math.max(0, Math.floor(progress)));
        if (status === "pending") {
            variant = "pending";
            label = "Pending";
        } else if (status === "paused") {
            variant = "pending";
            label = "Paused";
        }
        return (
            <div className={`bulk-status-pill bulk-status-${variant}`} aria-label={`${label} ${percent}%`}>
                <div className="bulk-status-pill-row">
                    <span>{label}</span>
                    <span>{percent}%</span>
                </div>
                <div className="bulk-status-pill-track">
                    <div className="bulk-status-pill-fill" style={{ width: `${percent}%` }} />
                </div>
            </div>
        );
    };

    const formatNumber = (value?: number | null) => {
        if (value === undefined || value === null || Number.isNaN(value)) return "0";
        return value.toLocaleString("en-US");
    };

    return (
        <>
            <div className="dashboard-page">
                <h1>Credit Usage Logs</h1>
                <p>Monitor credit usage across your team and review verification activity.</p>
            </div>

            <div className="row">
                <div className="col-md-10">
                    <div className="d-flex flex-row">
                        <div>
                            <img src="/images/credit-log.png" alt="" />
                        </div>
                        <div>
                            <p className="user-list">Credit Activity</p>
                        </div>
                    </div>

                    <div className="user-management-box">
                        <div className="d-flex flex-row justify-content-between align-items-start">
                            <button
                                type="button"
                                className="credit-logs-back"
                                onClick={() => navigate("/manage_users")}
                            >
                                <svg width="8" height="13" viewBox="0 0 8 13" aria-hidden="true">
                                    <path d="M6.5 1.5 1.5 6.5l5 5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                <span>Back</span>
                            </button>
                            <div className="search-wrapper">
                                <img
                                    src="/images/search-icon.png"
                                    alt="search"
                                    className="search-icon left"
                                />
                                <input
                                    type="text"
                                    className="search-input"
                                    placeholder="Search by user or email..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                />
                            </div>

                            {error && <div className="alert alert-danger mt-2 credit-logs-error">{error}</div>}
                        </div>

                        <div className="table-wrapper credit-logs-table-wrapper">
                            <table className="custom-table">
                                <thead>
                                    <tr>
                                        <th>Team Member</th>
                                        <th>Email</th>
                                        <th>Credits</th>
                                        <th>Date & Time</th>
                                        <th>Verification Summary</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan={5} style={{ textAlign: "center" }}>
                                                Loading credit logs...
                                            </td>
                                        </tr>
                                    ) : creditLogs.length === 0 ? (
                                        <tr>
                                            <td colSpan={5} style={{ textAlign: "center" }}>
                                                No credit logs found
                                            </td>
                                        </tr>
                                    ) : (
                                        creditLogs.map((log: any) => {
                                            const name = log.verified_by || '-';
                                            const emailOrFileName = log.email_or_file_name || '-';
                                            const used = log.credits_used || 0;
                                            const valid = Number(log?.verification_details?.valid || 0);
                                            const invalid = Number(log?.verification_details?.invalid || 0);
                                            const catchAll = Number(log?.verification_details?.catch_all || 0);
                                            const risky = Number(log?.verification_details?.risky || 0);
                                            const doNotMail = Number(log?.verification_details?.do_not_mail || 0);
                                            const invalidDomain = Number(log?.verification_details?.invalid_domain || 0);
                                            // Same six outcomes and icons as the Email Verifier page;
                                            // each count shows on hover.
                                            // `color` matches each icon, for the printed count.
                                            const outcomes = [
                                                { label: "Valid", icon: "validate.svg", count: valid, tone: "valid", color: "#188240" },
                                                { label: "Invalid", icon: "invalid.svg", count: invalid, tone: "invalid", color: "#DF5D5D" },
                                                { label: "Catch-All", icon: "catch-all.svg", count: catchAll, tone: "catchall", color: "#A855F7" },
                                                { label: "Do Not Mail", icon: "do-not-sent.svg", count: doNotMail, tone: "catch", color: "#6B7280" },
                                                { label: "Invalid Domain", icon: "invalid-domain.svg", count: invalidDomain, tone: "invalid", color: "#EA6A1F" },
                                                { label: "Risky", icon: "risk.svg", count: risky, tone: "risky", color: "#D68A00" },
                                            ];
                                            // Counts are final only once the request has completed.
                                            const status = String(log.status || "completed").toLowerCase();
                                            const finished = status === "completed";
                                            const lastActive = formatTimestamp(log.last_active);

                                            return (
                                                <tr key={log.request_id}>
                                                    <td>{name}</td>
                                                    <td>{emailOrFileName}</td>
                                                    <td>{used}</td>
                                                    <td>{lastActive}</td>
                                                    <td>
                                                        {!finished ? (
                                                            renderStatusPill(status, Number(log.progress) || 0)
                                                        ) : (
                                                        <div className="d-flex flex-row credit-logs-details">
                                                            {outcomes.map((outcome) => (
                                                                <div
                                                                    key={outcome.label}
                                                                    className="icon-tooltip"
                                                                    tabIndex={0}
                                                                    aria-label={`${outcome.label}: ${formatNumber(outcome.count)}`}
                                                                >
                                                                    <img className="credit-logs-outcome-icon" src={`/images/${outcome.icon}`} alt="" />
                                                                    <span className="credit-logs-count" style={{ color: outcome.color }}>
                                                                        {formatNumber(outcome.count)}
                                                                    </span>
                                                                    <span className={`tooltip-text tooltip-${outcome.tone}`}>
                                                                        {outcome.label}: {formatNumber(outcome.count)}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {totalPages > 1 && (
                            <div className="pagination-container">
                                <button
                                    className="pagination-arrow"
                                    onClick={() => handlePageChange(page - 1)}
                                    disabled={page === 1}
                                >
                                    &lt;
                                </button>

                                {renderPageNumbers().map((pageNum, index) => (
                                    pageNum === "..." ? (
                                        <span key={`ellipsis-${index}`} className="pagination-ellipsis">...</span>
                                    ) : (
                                        <button
                                            key={pageNum}
                                            className={`pagination-number ${page === pageNum ? "active" : ""}`}
                                            onClick={() => handlePageChange(pageNum as number)}
                                        >
                                            {pageNum}
                                        </button>
                                    )
                                ))}

                                <button
                                    className="pagination-arrow"
                                    onClick={() => handlePageChange(page + 1)}
                                    disabled={page === totalPages}
                                >
                                    &gt;
                                </button>

                                <div className="go-to-page">
                                    <span>Go to page</span>
                                    <input
                                        type="number"
                                        min="1"
                                        max={totalPages}
                                        value={goToPageInput}
                                        onChange={(e) => setGoToPageInput(e.target.value)}
                                        onKeyPress={(e) => {
                                            if (e.key === "Enter") {
                                                handleGoToPage();
                                            }
                                        }}
                                        onBlur={handleGoToPage}
                                        className="page-input"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <div className="col-md-2">
                    <div className="user-management-box-1">
                        <p className="total-credits">
                            Total Logs
                        </p>
                        <span className="total-credits">{totalLogs}</span>
                        <div className="devider"></div>
                        <p className="total-credits">
                        Credits Used
                        </p>
                        <span className="total-credits-r">{totalCreditsUsed}</span>
                        <div className="devider"></div>

                        <p className="total-credits">
                        Active Users
                        </p>
                        <span className="total-credits-m">{activeUsers}</span>

                        <div className="devider"></div>
                        <p className="total-credits">
                        Inactive Users
                        </p>
                        <span className="total-credits-t">{inactiveUsers}</span>
                    </div>
                </div>
            </div>
        </>
    )
}

export default CreditLogs